import * as ActionTypes from './ActionTypes'

import { loadConfigSucceeded, loadConfigFailed } from '@ordernext/networking-stadium/stadium/actions'

import { FETCH_PROMOTIONS_SUCCEEDED, FETCH_PROMOTIONS_FAILED } from '../actions/promotion'
import { MENUS_SUCCEEDED, MENUS_FAILED } from '../actions/menus'
import { MENUS_ITEMS_SUCCEEDED, MENUS_ITEMS_FAILED } from '../actions/menus'

import { createDataDogLogNoHistory } from '../utils/datadog'

import { VNTAXES_FETCH_TAXES_SUCCEEDED } from '../VNTaxes/ActionTypes'

import { Map } from 'immutable'
import { isE700Device } from '../VNAndroidSDK/Utils'

const FIVE_MINUTES_IN_MS = 5 * 60 * 1000
const ONE_HOUR_IN_MS = 60 * 60 * 1000

const getInitialState = () => {
  return Map({
    dataLoadRequested: false,
    lastDataLoadInitiated: undefined,
    lastDataLoadCompleted: undefined,
    baseLoadRequested: false,
    shouldBaseLoadMenus: true,
    lastBaseLoadInitiated: undefined,
    lastBaseLoadCompleted: undefined,
    successCheck: 1,
    failureCheck: 1,
    sections: [],
  })
}

const logLoadCompleted = (action, state) => {
  createDataDogLogNoHistory('info', `VNDataLoadManager: ${action}`, { state: state.toJS() })
}

// Use math to keep track of the success and failure of each section loaded.
// This allows us to modular arithmetic to determine if a section has been loaded successfully or
// not and expand the checks easily in the future while keeping data compact.
// A section is considered loaded successfully if the check % primeNumber === 0
export const CONFIG_PRIME = 2
export const SECTIONS_PRIME = 3
export const PROMOTIONS_PRIME = 5
export const TAXES_PRIME = 7
export const MENUS_PRIME = 11
export const MENUS_ITEMS_PRIME = 13

const reducer = (state = getInitialState(), action) => {
  switch (action.type) {
    case ActionTypes.VNDATA_LOAD_MANAGER_START_DATA_LOAD: {
      // Note the conditions in this action set up whether the sync should run.
      const lastDataLoadInitiated = state.get('lastDataLoadInitiated')
      const lastDataLoadCompleted = state.get('lastDataLoadCompleted')

      const loadTiming = isE700Device() ? ONE_HOUR_IN_MS : FIVE_MINUTES_IN_MS

      // If there was a completed data load and the new request is "too soon", then consider it done.
      if (lastDataLoadCompleted && lastDataLoadInitiated > Date.now() - loadTiming) {
        return state.set('dataLoadRequested', false)
      }

      return state
        .set('dataLoadRequested', true)
        .set('lastDataLoadInitiated', undefined)
        .set('lastDataLoadCompleted', undefined)
        .set('successCheck', 1)
        .set('failureCheck', 1)
    }

    case ActionTypes.VNDATA_LOAD_MANAGER_SET_DATA_LOAD_INITIATED:
      // Note the conditions here set up whether the sync is running.
      return state.set('dataLoadRequested', false).set('lastDataLoadInitiated', Date.now())

    case ActionTypes.VNDATA_LOAD_MANAGER_END_DATA_LOAD:
      return state
        .set('dataLoadRequested', false)
        .set('lastDataLoadCompleted', Date.now())
        .set('successCheck', 1)
        .set('failureCheck', 1)

    case ActionTypes.VNDATA_LOAD_MANAGER_START_BASE_LOAD: {
      // Note the conditions in this action set up whether the sync should run.
      const lastBaseLoadInitiated = state.get('lastBaseLoadInitiated')
      const lastBaseLoadCompleted = state.get('lastBaseLoadCompleted')

      // If there was a completed data load and the new request is "too soon", then consider it done.
      if (lastBaseLoadCompleted && lastBaseLoadInitiated > Date.now() - FIVE_MINUTES_IN_MS) {
        return state.set('baseLoadRequested', false)
      }

      return state.set('baseLoadRequested', true).set('lastBaseLoadInitiated', undefined).set('lastBaseLoadCompleted', undefined)
    }

    case ActionTypes.VNDATA_LOAD_MANAGER_SET_BASE_LOAD_INITIATED:
      // Note the conditions here set up whether the sync is running.
      return state.set('baseLoadRequested', false).set('lastBaseLoadInitiated', Date.now())

    case ActionTypes.VNDATA_LOAD_MANAGER_END_BASE_LOAD:
      return state.set('baseLoadRequested', false).set('shouldBaseLoadMenus', false).set('lastBaseLoadCompleted', Date.now())

    case ActionTypes.VNDATA_LOAD_MANAGER_RESET_BASE_LOAD:
      return getInitialState()

    case ActionTypes.VNDATA_LOAD_MANAGER_SET_SECTIONS:
      return state.set('sections', action.sections).set('successCheck', state.get('successCheck') * SECTIONS_PRIME)

    case loadConfigSucceeded.type: {
      logLoadCompleted(action.type, state)
      return state.set('successCheck', state.get('successCheck') * CONFIG_PRIME)
    }

    case loadConfigFailed.type: {
      logLoadCompleted(action.type, state)
      return state.set('failureCheck', state.get('failureCheck') * CONFIG_PRIME)
    }

    case FETCH_PROMOTIONS_SUCCEEDED: {
      logLoadCompleted(action.type, state)
      return state.set('successCheck', state.get('successCheck') * PROMOTIONS_PRIME)
    }

    case FETCH_PROMOTIONS_FAILED: {
      logLoadCompleted(action.type, state)
      return state.set('failureCheck', state.get('failureCheck') * PROMOTIONS_PRIME)
    }

    case MENUS_SUCCEEDED: {
      logLoadCompleted(action.type, state)
      return state.set('successCheck', state.get('successCheck') * MENUS_PRIME)
    }

    case MENUS_FAILED: {
      logLoadCompleted(action.type, state)
      return state.set('failureCheck', state.get('failureCheck') * MENUS_PRIME)
    }

    case MENUS_ITEMS_SUCCEEDED: {
      logLoadCompleted(action.type, state)
      return state.set('successCheck', state.get('successCheck') * MENUS_ITEMS_PRIME)
    }

    case MENUS_ITEMS_FAILED: {
      logLoadCompleted(action.type, state)
      return state.set('failureCheck', state.get('failureCheck') * MENUS_ITEMS_PRIME)
    }

    case VNTAXES_FETCH_TAXES_SUCCEEDED: {
      logLoadCompleted(action.type, state)
      return state.set('successCheck', state.get('successCheck') * TAXES_PRIME)
    }

    default:
      return state
  }
}

export default reducer
